import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ICreateNoteProps } from "../../../../../../@types/Note";
import { IPatientSearchListItem } from "../../../../../../@types/Patient";
import { ITaggableHcp } from "../../../../../../@types/Staff";
import { Button } from "../../../../../../components/Button";
import { RichTextEditor } from "../../../../../../components/RichTextEditor";
import { SelectDropdown } from "../../../../../../components/SelectDropdown";
import { cleanHTML } from "../../../../../../hooks/queries/notes";
import { usePatientSearchList } from "../../../../../../hooks/queries/patients";
import { useTaggableHcps } from "../../../../../../hooks/queries/staff";
import { noteService } from "../../../../../../services/noteService";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { buildHcpMentionConfig } from "../../../../../../utils/noteMentionConfig";
import { extractTaggedHcpIds } from "../../../../../../utils/noteMentions";
import { NoteAssignmentToolbar } from "../NoteAssignmentToolbar";
import {
  CancelButton,
  Container,
  PatientPickerLabel,
  PatientPickerRow,
  SubmitContainer,
  TextAreaContainer,
  Title,
} from "./CreateNoteModal.styles";

type CreateNoteModalProps = {
  // When provided, the patient is fixed and the picker is hidden. When
  // omitted, the modal shows a patient picker (My Tasks "Add task" entry
  // point on the alerts page).
  patientId?: number;
  // When true, the modal opens locked into task mode (assign toggle is
  // active and disabled). Used by the standalone "Add task" entry point.
  forceTaskMode?: boolean;
  setNotification: (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => void;
  onClose: () => void;
};

export function CreateNoteModal({
  patientId: fixedPatientId,
  forceTaskMode = false,
  setNotification,
  onClose,
}: CreateNoteModalProps) {
  const { t } = useTranslation();

  const [noteContent, setNoteContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [assignedHcpId, setAssignedHcpId] = useState<number | null>(null);
  const [isUrgent, setIsUrgent] = useState(false);
  // Selected patient when the picker is shown. Null until the user chooses.
  const [pickedPatientId, setPickedPatientId] = useState<number | null>(null);

  // Task mode is derived from whether an assignee is picked. forceTaskMode is
  // used by the standalone My Tasks entry point to require an assignee at save.
  const isTaskMode = forceTaskMode || assignedHcpId !== null;

  const showsPatientPicker = fixedPatientId === undefined;
  const effectivePatientId = fixedPatientId ?? pickedPatientId ?? undefined;

  const { patients } = usePatientSearchList();
  const { data: taggableHcps } = useTaggableHcps(effectivePatientId);

  // Quill instantiates once and captures the source callback in a closure, so
  // the callback must read the latest data via refs — not via values at mount.
  const taggableHcpsRef = useRef<ITaggableHcp[]>([]);
  useEffect(() => {
    taggableHcpsRef.current = taggableHcps ?? [];
  }, [taggableHcps]);

  // Reset the assignee whenever the selected patient changes — the previous
  // HCP may not be accessible on the new patient's ward.
  useEffect(() => {
    if (showsPatientPicker) {
      setAssignedHcpId(null);
    }
  }, [pickedPatientId, showsPatientPicker]);

  const noteContentRef = useRef("");

  const handleInputChange = (value: string) => {
    setNoteContent(value);
    noteContentRef.current = value;
  };

  const mentionConfig = useMemo(
    () =>
      buildHcpMentionConfig(
        () => taggableHcpsRef.current,
        () => new Set(extractTaggedHcpIds(noteContentRef.current))
      ),
    []
  );

  const patientOptions = useMemo(
    () =>
      patients.map((p: IPatientSearchListItem) => {
        const fullName = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
        const tail = p.mrn ? ` · ${p.mrn}` : "";
        return {
          label: `${
            fullName || t("patientPage_notes_patientFallback", { id: p.id })
          }${tail}`,
          value: String(p.id),
          key: String(p.id),
        };
      }),
    [patients, t]
  );

  const handleClearAssignee = () => {
    setAssignedHcpId(null);
  };

  const handleCreateNote = async () => {
    if (isSaving) return;
    if (effectivePatientId === undefined) {
      setNotification(t("notes_errors_selectPatient"), "warning");
      return;
    }
    if (forceTaskMode && assignedHcpId === null) {
      setNotification(t("notes_errors_selectHcp"), "warning");
      return;
    }
    setIsSaving(true);
    const cleanNoteContent: string = await cleanHTML(noteContent);
    const taggedHcpIds = extractTaggedHcpIds(cleanNoteContent);
    mixpanelActions.track(
      forceTaskMode ? "User Action: Save Task" : "User Action: Save Note"
    );

    const dataToCreateNote: ICreateNoteProps = {
      noteContent: cleanNoteContent,
      patientId: effectivePatientId,
      taggedHcpIds: taggedHcpIds.length > 0 ? taggedHcpIds : undefined,
      assignedHcpId: assignedHcpId ?? null,
      // Urgent is task-only — coerce to false on plain notes so a stale
      // toggle from a brief task mode doesn't persist as `is_urgent=true`
      // on a note. The backend doesn't enforce this today.
      isUrgent: isTaskMode ? isUrgent : false,
    };

    try {
      const { data } = await noteService.createNote(dataToCreateNote);
      if (data) {
        setNotification(
          isTaskMode
            ? t("notes_toasts_taskAdded")
            : t("notes_toasts_noteAdded"),
          "success"
        );
      } else {
        setNotification(t("notes_errors_addFailed"), "error");
      }
      onClose();
    } catch {
      setNotification(t("notes_errors_addFailed"), "error");
      setIsSaving(false);
    }
  };

  const handleCancelClick = () => {
    onClose();
  };

  return (
    <Container>
      <Title>{forceTaskMode ? t("notes_addTask") : t("notes_addNote")}</Title>
      {showsPatientPicker && (
        <PatientPickerRow>
          <PatientPickerLabel>{t("notes_patient")}</PatientPickerLabel>
          <SelectDropdown
            options={patientOptions}
            value={
              pickedPatientId !== null ? String(pickedPatientId) : undefined
            }
            placeholder={t("notes_selectPatient")}
            showSearch
            optionFilterProp="label"
            width={320}
            height={36}
            onValueChange={(selected) => {
              const value = Array.isArray(selected) ? selected[0] : selected;
              setPickedPatientId(value ? Number(value) : null);
            }}
          />
        </PatientPickerRow>
      )}
      <TextAreaContainer>
        <NoteAssignmentToolbar
          taggableHcps={taggableHcps}
          assignedHcpId={assignedHcpId}
          isUrgent={isUrgent}
          assignLabel={forceTaskMode ? t("notes_assign") : undefined}
          // Urgent only makes sense on tasks — show the toggle once we're in
          // task mode (assignee picked, or forceTaskMode for the My Tasks
          // entry point where the modal is always a task).
          showUrgentToggle={isTaskMode}
          onAssignToggle={handleClearAssignee}
          onUrgentToggle={() => setIsUrgent((v) => !v)}
          onAssigneeChange={(hcpId) => setAssignedHcpId(hcpId)}
        />
        <RichTextEditor
          value={noteContent}
          height={300}
          placeholderTxt={
            forceTaskMode
              ? t("notes_taskPlaceholder")
              : t("notes_notePlaceholder")
          }
          onValueChange={(value) => handleInputChange(value)}
          mentionConfig={mentionConfig}
        />
      </TextAreaContainer>
      <SubmitContainer>
        <Button
          label={isTaskMode ? t("notes_saveTask") : t("notes_saveNote")}
          type="button"
          onClick={handleCreateNote}
          isLoading={isSaving}
        />
        <CancelButton onClick={handleCancelClick} disabled={isSaving}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </Container>
  );
}
